// Flagged gaps for the selected read (truth set, parts 4 and 5): every fact
// that answers in this read contradict, with what the truth set says, what
// the answers said, which models said it, the share of answers, a sentence,
// and (where a lane cites its sources) the third-party pages carrying the
// wrong value. Admins also see candidate facts (claims no confirmed fact
// covers) and the controls to check or re-check the read.

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, Loader2, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import type { FlaggedGap, ReadTruthCheck } from '@/api/deep-signal-api';
import {
  addManualFact, cancelRunCheck, getRunChecks, launchRunCheck, resumeRunCheck, type RunChecks,
} from '@/api/truth-set';

const muted: React.CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--text-xs)' };
const box: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 8, padding: 12,
                                   backgroundColor: 'var(--background)' };
const small: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
  fontSize: 'var(--text-xs)', fontWeight: 600, border: '1px solid var(--border)', background: 'transparent',
  color: 'var(--foreground)',
};

function pct(share: number | null | undefined): string {
  return share == null ? 'Not measured' : `${(share * 100).toFixed(1)}%`;
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** "Gemini and Perplexity give 2019 as your founding year; both cite x.com." */
export function gapSentence(gap: FlaggedGap): string {
  const who = joinNames(gap.lanes.map((l) => l.label));
  let what = gap.attribute_noun || (gap.attribute_label || gap.attribute).toLowerCase();
  if (gap.subject_type !== 'brand') what += ` of ${gap.subject_name}`;
  if (gap.qualifier) what += ` (${gap.qualifier})`;
  const verb = gap.lanes.length === 1 ? 'gives' : 'give';
  let sentence = `${who} ${verb} ${gap.answer_value} as your ${what}`;
  const carriers = gap.carriers || [];
  if (carriers.length) {
    const hosts = carriers.slice(0, 3).map((c) => { try { return new URL(c.url).hostname.replace(/^www\./, ''); } catch { return c.url; } });
    sentence += `; ${gap.lanes.length > 1 ? 'they cite' : 'it cites'} ${joinNames(hosts)}`;
  }
  return `${sentence}.`;
}

export function FlaggedGapsSection({ customerId, runId, check, isAdmin, onChanged }: {
  customerId: number;
  runId: number | null;
  check: ReadTruthCheck | null | undefined;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [checks, setChecks] = useState<RunChecks | null>(null);
  const [busy, setBusy] = useState(false);

  const loadChecks = useCallback(async () => {
    if (!isAdmin || runId == null) return;
    try { setChecks(await getRunChecks(customerId, runId)); } catch { setChecks(null); }
  }, [customerId, runId, isAdmin]);

  useEffect(() => { loadChecks(); }, [loadChecks]);
  useEffect(() => {
    if (!checks?.live) return;
    const timer = window.setInterval(async () => {
      await loadChecks();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [checks?.live, loadChecks]);
  useEffect(() => {
    // A check that just finished changes the read's payload.
    if (checks && !checks.live) onChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checks?.live?.id]);

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try { await fn(); toast.success(done); await loadChecks(); }
    catch (e: any) { toast.error(e?.message || 'That did not work.'); }
    finally { setBusy(false); }
  };

  const gaps = check?.gaps || [];
  const live = checks?.live;
  const lastCheck = checks?.checks.find((c) => c.status !== 'pending' && c.status !== 'running');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <AlertTriangle className="w-4 h-4" style={{ color: 'var(--infrared-coral)' }} />
        <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Flagged gaps in this read</span>
        {check && <span style={muted}>checked against truth set v{check.version_number}</span>}
      </div>

      {isAdmin && runId != null && (
        <div style={{ ...muted, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {live ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              Checking {live.answers_checked + live.answers_failed} of {live.answers_total} answers · ${live.cost_usd.toFixed(2)}
              <button type="button" style={small} disabled={busy}
                      onClick={() => act(() => cancelRunCheck(live.id, !live.cancel_requested), live.cancel_requested ? 'Resumed' : 'Cancelling')}>
                {live.cancel_requested ? 'Undo cancel' : 'Cancel'}
              </button>
            </>
          ) : (
            <>
              {checks?.stale && <span style={{ color: 'var(--infrared-coral)' }}>A newer truth set (v{checks.latest_version}) exists.</span>}
              {lastCheck && lastCheck.stop_reason && lastCheck.stop_reason !== 'done' && (
                <span>Last check stopped ({lastCheck.stop_reason}).{' '}
                  <button type="button" style={small} disabled={busy}
                          onClick={() => act(() => resumeRunCheck(lastCheck.id), 'Resumed')}>Resume</button>
                </span>
              )}
              {checks?.latest_version != null && (
                <button type="button" style={small} disabled={busy}
                        onClick={() => act(() => launchRunCheck(customerId, runId), 'Check started')}>
                  <RefreshCw className="w-3 h-3" /> {check ? `Re-check against v${checks.latest_version}` : 'Check this read'}
                </button>
              )}
              {checks && checks.latest_version == null && <span>Confirm a truth set to check reads against it.</span>}
            </>
          )}
        </div>
      )}

      {!check ? (
        <p style={muted}>This read has not been checked against a confirmed truth set yet.</p>
      ) : gaps.length === 0 ? (
        <p style={muted}>No answer in this read contradicts a confirmed fact.</p>
      ) : (
        gaps.map((gap, i) => (
          <div key={i} style={box}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{gapSentence(gap)}</span>
              {gap.status && (
                <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, padding: '1px 8px', borderRadius: 999,
                               border: '1px solid var(--border)',
                               color: gap.status.status === 'persisting' ? 'var(--infrared-coral)' : 'var(--foreground)' }}>
                  {gap.status.label}
                  {gap.status.status === 'persisting' && gap.status.reads_elapsed
                    ? `, ${gap.status.reads_elapsed} read${gap.status.reads_elapsed === 1 ? '' : 's'} after the fix` : ''}
                </span>
              )}
              <span style={{ ...muted, marginLeft: 'auto' }}
                    title={isAdmin && gap.answers != null ? `${gap.answers} of ${gap.answers_checked} answers` : undefined}>
                {pct(gap.share)} of answers
              </span>
            </div>
            <div style={{ ...muted, marginTop: 2 }}>Your confirmed fact: {gap.truth_value}</div>
            {gap.examples.map((e, j) => (
              <div key={j} style={{ fontSize: 'var(--text-xs)', marginTop: 4 }}>
                <span style={{ fontWeight: 600 }}>{e.lane_label}: </span>
                <span style={{ fontStyle: 'italic' }}>“{e.sentence}”</span>
              </div>
            ))}
            {(gap.carriers || []).length > 0 && (
              <div style={{ marginTop: 6 }}>
                <span style={{ ...muted, fontWeight: 600 }}>Where the wrong value lives</span>
                {gap.carriers!.map((c) => (
                  <div key={c.url} style={{ fontSize: 'var(--text-xs)', display: 'flex', gap: 6, alignItems: 'baseline' }}>
                    <a href={c.url} target="_blank" rel="noreferrer noopener"
                       style={{ color: 'var(--spectral-violet)', wordBreak: 'break-all' }}>
                      {c.title || c.url} <ExternalLink className="inline w-3 h-3" />
                    </a>
                    {c.own_site && <span style={{ ...muted, fontWeight: 600 }}>your own site</span>}
                    {c.quote && <span style={{ ...muted, fontStyle: 'italic' }}>“{c.quote}”</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}

      {isAdmin && (check?.candidates || []).length > 0 && (
        <div style={box}>
          <span style={{ ...muted, fontWeight: 600 }}>Candidate facts (answers state these; no confirmed fact covers them)</span>
          {check!.candidates!.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 'var(--text-xs)', marginTop: 4 }}>
              <span style={{ fontWeight: 600, minWidth: 140 }}>
                {c.attribute_label || c.attribute}{c.subject_type !== 'brand' ? ` (${c.subject_name})` : ''}
                {c.qualifier ? `, ${c.qualifier}` : ''}
              </span>
              <span>{c.value}</span>
              <span style={muted} title={c.example}>{c.answers} answers · {c.lanes.map((l) => l.label).join(', ')}</span>
              <button type="button" style={{ ...small, marginLeft: 'auto' }} disabled={busy}
                      title="Add to the truth set as a fact a person confirms (it takes effect at the next confirmed version)"
                      onClick={() => act(() => addManualFact(customerId, {
                        attribute: c.attribute, value: c.value, subject_type: c.subject_type,
                        subject: c.subject_type === 'brand' ? undefined : c.subject_name,
                        qualifier: c.qualifier || undefined, note: 'Added from a read\'s candidate facts',
                      }), 'Added to the truth set (confirm a new version to use it)')}>
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
